// product/ui/src/shared/CtaButton.tsx
//
// Clickable affordance. Two forms:
//   - href present → <a> (deep link; opens in new tab per planning/02-impl-
//     chat-surface.md §D.2 — widgets can't persist state across nav).
//   - href absent  → <button>.
//
// Dumb. No variant system, no loading state, no icons. Swoop's brand team
// customises post-M5 via the extension surface (D.t8).

import type { MouseEventHandler, ReactNode } from "react";

type Common = {
  children: ReactNode;
  className?: string;
  /** Disabled state only applies to the button variant. */
  disabled?: boolean;
  ariaLabel?: string;
};

type ButtonProps = Common & {
  href?: undefined;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit";
};

type LinkProps = Common & {
  href: string;
  onClick?: undefined;
  type?: undefined;
};

export type CtaButtonProps = ButtonProps | LinkProps;

const baseClass = [
  "group/cta inline-flex items-center justify-center gap-1.5",
  "rounded-swoop border border-swoop-accent bg-swoop-accent",
  "px-3.5 py-2 text-[13px] font-semibold uppercase tracking-[0.05em] text-swoop-accent-fg",
  "shadow-sm transition-colors duration-150",
  // swoop-patagonia.com's own `.btn:hover` move: the fill drops to white and
  // the text + border take the accent. Works against any accent override.
  "hover:bg-white hover:text-swoop-accent",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-swoop-accent focus-visible:ring-offset-2",
  "motion-safe:active:scale-[0.98]",
  // pointer-events-none keeps the inversion hover off disabled buttons
  // (anchors are never disabled, so this only bites the <button> form).
  "disabled:cursor-not-allowed disabled:opacity-60 disabled:pointer-events-none",
].join(" ");

export function CtaButton(props: CtaButtonProps) {
  if ("href" in props && props.href) {
    const { href, children, className, ariaLabel } = props;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={[baseClass, className ?? ""].join(" ")}
        aria-label={ariaLabel}
      >
        {children}
        <span
          aria-hidden="true"
          className="transition-transform duration-200 motion-safe:group-hover/cta:translate-x-0.5"
        >
          →
        </span>
      </a>
    );
  }

  const { onClick, children, className, disabled, type = "button", ariaLabel } =
    props as ButtonProps;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[baseClass, className ?? ""].join(" ")}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

CtaButton.displayName = "CtaButton";
